/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceEventNoteProps {
  id: string;
  outOfServiceEventId: string;
  content: string;
  noteTime: Date;
  authorId: string | null;
  noteCategory: string | null;
  noteMetadataJson: string | null;
  isSystem: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OutOfServiceEventNoteRecord extends Record<OutOfServiceEventNoteProps>({
  id: null as any,
  outOfServiceEventId: null as any,
  content: null as any,
  noteTime: null as any,
  authorId: null as any,
  noteCategory: null as any,
  noteMetadataJson: null as any,
  isSystem: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    outOfServiceEventId: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OutOfServiceEventNoteRecord {
    if (json == null) {
      throw new Error('OutOfServiceEventNoteRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceEventNoteRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventNoteJson.id',
      ),
      outOfServiceEventId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'outOfServiceEventId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventNoteJson.outOfServiceEventId',
      ),
      content: getFromOrThrow(json, (j) => j.content, 'OutOfServiceEventNoteJson.content'),
      noteTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
          'OutOfServiceEventNoteJson.noteTime',
        ),
      ),
      authorId: json.authorId,
      noteCategory: json.noteCategory,
      noteMetadataJson: json.noteMetadataJson,
      isSystem: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isSystem', false),
        'OutOfServiceEventNoteJson.isSystem',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventNoteJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    outOfServiceEventId: string;
    content: string;
    noteTime: string | number | Date;
    authorId: string | null;
    noteCategory: string | null;
    noteMetadataJson: string | null;
    isSystem: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OutOfServiceEventNoteRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceEventNoteRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceEventNoteRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventNoteJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceEventId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'outOfServiceEventId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventNoteJson.outOfServiceEventId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'OutOfServiceEventNoteJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'noteTime', '0001-01-01T00:00:00.00Z'),
              'OutOfServiceEventNoteJson.noteTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteCategory: tryOrDefault(
        () => json.noteCategory,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      noteMetadataJson: tryOrDefault(
        () => json.noteMetadataJson,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isSystem: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isSystem', false),
            'OutOfServiceEventNoteJson.isSystem',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventNoteJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceEventNoteProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceEventNoteRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceEventNoteViewModelBase implements OutOfServiceEventNoteProps {
  protected readonly _props: OutOfServiceEventNoteProps;
  private _json?: OutOfServiceEventNoteProps;

  get id(): string {
    return this._props.id;
  }
  get outOfServiceEventId(): string {
    return this._props.outOfServiceEventId;
  }
  get content(): string {
    return this._props.content;
  }
  get noteTime(): Date {
    return this._props.noteTime;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get noteCategory(): string | null {
    return this._props.noteCategory;
  }
  get noteMetadataJson(): string | null {
    return this._props.noteMetadataJson;
  }
  get isSystem(): boolean {
    return this._props.isSystem;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: OutOfServiceEventNoteProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        outOfServiceEventId: this._props.outOfServiceEventId,
        content: this._props.content,
        noteTime: this._props.noteTime,
        authorId: this._props.authorId,
        noteCategory: this._props.noteCategory,
        noteMetadataJson: this._props.noteMetadataJson,
        isSystem: this._props.isSystem,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOutOfServiceEventNoteEntities = (state: AppState) =>
  state.entityStores.outOfServiceEventNote.entities;

export const selectOutOfServiceEventNoteById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceEventNote.entities.get(id);

export const selectOutOfServiceEventNoteMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceEventNote.meta.get(id);

export const selectOutOfServiceEventNoteAndMetadataById = (id: string) =>
  createSelector(
    selectOutOfServiceEventNoteById(id),
    selectOutOfServiceEventNoteMetadataById(id),
    (outOfServiceEventNote, meta) => ({ outOfServiceEventNote, meta }),
  );

export function selectOutOfServiceEventNoteEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOutOfServiceEventNoteEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OutOfServiceEventNoteRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOutOfServiceEventNoteEntitiesByOutOfServiceEventId(
  outOfServiceEventId: string,
) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.outOfServiceEventId === outOfServiceEventId).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByContent(content: string) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByNoteTime(noteTime: Date) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.noteTime === noteTime).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByNoteCategory(noteCategory: string) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.noteCategory === noteCategory).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByNoteMetadataJson(noteMetadataJson: string) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.noteMetadataJson === noteMetadataJson).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByIsSystem(isSystem: boolean) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.isSystem === isSystem).valueSeq(),
  );
}

export function selectOutOfServiceEventNoteEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllOutOfServiceEventNoteEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
